import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Space,
  Tag,
  Tooltip,
  message,
  Statistic,
  Progress,
  Spin,
  List,
  Avatar,
  Typography,
  Divider
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  EyeOutlined, 
  DeleteOutlined,
  ClockCircleOutlined,
  UserOutlined,
  ProjectOutlined,
  CheckCircleOutlined,
  MessageOutlined,
  SendOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;

const TaskManagement = ({ currentUser, selectedProjectId = null, compactView = false }) => {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [taskStats, setTaskStats] = useState({});
  const [viewingTask, setViewingTask] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [taskComments, setTaskComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [form] = Form.useForm();
  const [commentForm] = Form.useForm();

  useEffect(() => {
    fetchProjects();
    fetchEmployees();
    fetchTaskStats();
  }, []);

  useEffect(() => {
    // If selectedProjectId is provided (compact view), use it directly
    if (selectedProjectId) {
      setSelectedProject(selectedProjectId);
      fetchTasks(selectedProjectId);
    } else if (selectedProject) {
      fetchTasks(selectedProject);
    }
  }, [selectedProject, selectedProjectId]);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/projects`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const response_data = await response.json();
      
      // Handle wrapped response format
      const data = response_data.data || response_data;
      console.log(data,"data=====================>");
      
      // Ensure data is an array
      if (!Array.isArray(data)) {
        console.error('Projects data is not an array:', data);
        setProjects([]);
        return;
      }
      
      // Filter projects where current user is PM or Tech Manager
      const userProjects = data.filter(project => 
        project.projectManager?.some(manager => manager._id === currentUser.id) ||
        project.techManager?.some(manager => manager._id === currentUser.id)
      );
      
      setProjects(userProjects);
      if (userProjects.length > 0) {
        setSelectedProject(userProjects[0]._id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      message.error('Failed to fetch projects');
      setProjects([]);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employees`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const response_data = await response.json();
      
      // Handle wrapped response format
      const data = response_data.data || response_data;
      
      // Ensure data is an array
      if (!Array.isArray(data)) {
        console.error('Employees data is not an array:', data);
        setEmployees([]);
        return;
      }
      
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  };

  const fetchTasks = async (projectId) => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tasks/project/${projectId}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const response_data = await response.json();
      
      // Handle wrapped response format
      const data = response_data.data || response_data;
    
      // Ensure data is an array
      if (!Array.isArray(data)) {
        console.error('Tasks data is not an array:', data);
        setTasks([]);
        return;
      }
      
      setTasks(data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      message.error('Failed to fetch tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };
 
  
  const fetchTaskStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tasks/stats/overview`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setTaskStats(data || {});
    } catch (error) {
      console.error('Error fetching task stats:', error);
      setTaskStats({});
    }
  };

  const fetchTaskComments = async (taskId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tasks/${taskId}/taskcomments`);
      if (response.ok) {
        const response_data = await response.json();
        const data = response_data.data || response_data;
        setTaskComments(Array.isArray(data) ? data : []);
      } else {
        setTaskComments([]);
      }
    } catch (error) {
      console.error('Error fetching task comments:', error);
      setTaskComments([]);
    }
  };

  const handleAddComment = async (values) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tasks/${viewingTask._id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          comment: values.comment,
          userId: currentUser.id,
          userName: currentUser.name
        }),
      });

      if (response.ok) {
        message.success('Comment added successfully');
        commentForm.resetFields();
        fetchTaskComments(viewingTask._id);
        // Refresh tasks to update comment count
        if (selectedProjectId) {
          fetchTasks(selectedProjectId);
        } else if (selectedProject) {
          fetchTasks(selectedProject);
        }
      } else {
        message.error('Failed to add comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      message.error('Failed to add comment');
    }
  };

  const handleCreateTask = () => {
    setEditingTask(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleViewTask = (task) => {
    setViewingTask(task);
    fetchTaskComments(task._id);
    setViewModalVisible(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task); 
    form.setFieldsValue({
      title: task.title,
      description: task.description,
      assignedTo: task.assignedTo._id,
      priority: task.priority,
      estimatedHours: task.estimatedHours,
      dueDate: task.dueDate ? dayjs(task.dueDate) : null,
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    try {
      const taskData = {
        ...values,
        dueDate: values.dueDate ? values.dueDate.toISOString() : null,
        createdBy: currentUser.id,
        projectId: selectedProject
      };

      const url = editingTask 
        ? `${process.env.REACT_APP_API_BASE_URL}/api/tasks/${editingTask._id}` 
        : `${process.env.REACT_APP_API_BASE_URL}/api/tasks`;
      
      const method = editingTask ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(taskData),
      });

      if (response.ok) {
        message.success(`Task ${editingTask ? 'updated' : 'created'} successfully`);
        setModalVisible(false);
        fetchTasks(selectedProject);
        fetchTaskStats();
      } else {
        const error = await response.json();
        message.error(error.message || 'Failed to save task');
      }
    } catch (error) {
      console.error('Error saving task:', error);
      message.error('Failed to save task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tasks/${taskId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        message.success('Task deleted successfully');
        fetchTasks(selectedProject);
        fetchTaskStats();
      } else {
        message.error('Failed to delete task');
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      message.error('Failed to delete task');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'orange',
      'in-progress': 'blue',
      'completed': 'green',
      'cancelled': 'red'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'green',
      'medium': 'orange',
      'high': 'red',
      'urgent': 'purple'
    };
    return colors[priority] || 'default';
  };

 const getProjectTeamMembers = () => {
  if (!selectedProject) return [];

  const project = projects.find(p => p._id === selectedProject);
  if (!project) return [];

  const teamMemberIds = [
    project.projectManager?._id,
    project.techManager?._id,
    ...(project.teamMembers || [])
      .map(member => member?.employeeId?._id) // safe chaining
      .filter(Boolean), // remove null/undefined
  ].filter(Boolean);

  return employees.filter(emp => teamMemberIds.includes(emp._id));
};


  const columns = [
    {
      title: 'Task Details',
      dataIndex: 'title',
      key: 'title',
      width: 250,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
            {record.description?.substring(0, 80)}...
          </div>
          <div style={{ fontSize: '11px', color: '#999' }}>
            ID: {record._id?.substring(0, 8)}...
          </div>
        </div>
      ),
    },
    {
      title: 'Team Member',
      dataIndex: 'assignedToName',
      key: 'assignedTo',
      width: 180,
      render: (text, record) => {
        const project = projects.find(p => p._id === selectedProject);
        const isProjectManager = project?.projectManager?._id === record.assignedTo;
        const isTechManager = project?.techManager?._id === record.assignedTo;
        
        let role = 'Team Member';
        if (isProjectManager) role = 'Project Manager';
        else if (isTechManager) role = 'Tech Manager';
        
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>
              <UserOutlined style={{ marginRight: 4 }} />
              {text}
            </div>
            <div style={{ fontSize: '11px', color: '#666' }}>
              {role}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Allocation & Hours',
      key: 'allocation',
      width: 150,
      render: (_, record) => {
        const project = projects.find(p => p._id === selectedProject);
        const teamMember = project?.teamMembers?.find(
          member => member.employeeId._id === record.assignedTo
        );
        const allocation = teamMember?.allocation || 100;
        
        return (
          <div>
            <div style={{ fontSize: '12px', marginBottom: 2 }}>
              <strong>Allocation:</strong> {allocation}%
            </div>
            <div style={{ fontSize: '12px', marginBottom: 2 }}>
              <strong>Estimated:</strong> {record.estimatedHours || 0}h
            </div>
            <div style={{ fontSize: '12px' }}>
              <strong>Actual:</strong> {record.actualHours || 0}h
            </div>
          </div>
        );
      },
    },
    {
      title: 'Status & Priority',
      key: 'statusPriority',
      width: 140,
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: 4 }}>
            <Tag color={getStatusColor(record.status)} size="small">
              {record.status.toUpperCase()}
            </Tag>
          </div>
          <div>
            <Tag color={getPriorityColor(record.priority)} size="small">
              {record.priority.toUpperCase()}
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Due Date & Progress',
      key: 'dueDateProgress',
      width: 160,
      render: (_, record) => {
        const progress = record.estimatedHours > 0 
          ? Math.min((record.actualHours / record.estimatedHours) * 100, 100) 
          : 0;
        
        const isOverdue = record.dueDate && dayjs(record.dueDate).isBefore(dayjs(), 'day');
        
        return (
          <div>
            <div style={{ 
              fontSize: '12px', 
              marginBottom: 4,
              color: isOverdue ? '#ff4d4f' : '#666'
            }}>
              {record.dueDate ? dayjs(record.dueDate).format('MMM DD, YYYY') : 'No due date'}
              {isOverdue && <span style={{ marginLeft: 4 }}>⚠️</span>}
            </div>
            <Progress 
              percent={Math.round(progress)} 
              size="small" 
              status={progress > 100 ? 'exception' : 'normal'}
              showInfo={false}
            />
            <div style={{ fontSize: '10px', color: '#999', textAlign: 'center' }}>
              {Math.round(progress)}%
            </div>
          </div>
        );
      },
    },
    {
      title: 'Comments',
      key: 'comments',
      width: 80,
      render: (_, record) => (
        console.log(record,"record==========>"),
        
        <Button 
          type="text" 
          icon={<MessageOutlined />}
          onClick={() => handleViewTask(record)}
          style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          {record.comments.length || 0}
        </Button>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => handleViewTask(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Task">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEditTask(record)}
            />
          </Tooltip>
          <Tooltip title="Delete Task">
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => handleDeleteTask(record._id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

const selectedProjectData = projects.find(p => p._id === selectedProject);

return (
  <div style={{ padding: compactView ? '12px' : '24px' }}>
    {!compactView && (
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Tasks"
              value={taskStats.totalTasks || 0}
              prefix={<ProjectOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="In Progress"
              value={taskStats.inProgressTasks || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Completed"
              value={taskStats.completedTasks || 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Overdue"
              value={taskStats.overdueTasks || 0}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>
    )}

    <Card
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{compactView ? 'Project Tasks' : 'Task Management'}</span>
          <Space>
            {!selectedProjectId && (
              <Select
                placeholder="Select Project"
                style={{ width: 200 }}
                value={selectedProject}
                onChange={setSelectedProject}
              >
                {projects.map(project => (
                  <Option key={project._id} value={project._id}>
                    {project.name}
                  </Option>
                ))}
              </Select>
            )}
            <Button type="primary" onClick={handleCreateTask} size={compactView ? 'small' : 'default'}>
              Create Task
            </Button>
          </Space>
        </div>
      }
    >
      {selectedProjectData && (
        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
          <strong>Project: {selectedProjectData.name}</strong>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
            Client: {selectedProjectData.client} | 
            PM: {selectedProjectData.projectManager?.map(pm => pm.name).join(', ')} | 
            Tech Manager: {selectedProjectData.techManager?.map(tm => tm.name).join(', ')}
          </div>
        </div>
      )}

      {compactView ? (
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin />
            </div>
          ) : tasks.length > 0 ? (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {tasks.map(task => (
                <Card 
                  key={task._id} 
                  size="small" 
                  style={{ marginBottom: '8px' }}
                  bodyStyle={{ padding: '12px' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        {task.title}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
                        Assigned to: {task.assignedToName}
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <Tag color={getStatusColor(task.status)} size="small">
                          {task.status.toUpperCase()}
                        </Tag>
                        <Tag color={getPriorityColor(task.priority)} size="small">
                          {task.priority.toUpperCase()}
                        </Tag>
                        {task.dueDate && (
                          <Tag color={dayjs(task.dueDate).isBefore(dayjs(), 'day') ? 'red' : 'blue'} size="small">
                            Due: {dayjs(task.dueDate).format('MMM DD')}
                          </Tag>
                        )}
                        <Button 
                          type="text" 
                          size="small" 
                          icon={<MessageOutlined />}
                          onClick={() => handleViewTask(task)}
                          style={{ padding: '2px 4px', height: 'auto' }}
                        >
                          {task.commentCount || 0}
                        </Button>
                      </div>
                    </div>
                    <div style={{ marginLeft: '12px', textAlign: 'right' }}>
                      <div style={{ fontSize: '11px', color: '#999' }}>
                        {task.estimatedHours > 0 && (
                          <div>
                            {task.actualHours || 0}h / {task.estimatedHours}h
                          </div>
                        )}
                      </div>
                      <Progress 
                        percent={task.estimatedHours > 0 ? Math.round((task.actualHours / task.estimatedHours) * 100) : 0}
                        size="small"
                        showInfo={false}
                        style={{ width: '60px', marginTop: '4px' }}
                      />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
              <CheckCircleOutlined style={{ fontSize: '24px', marginBottom: '8px' }} />
              <div>No tasks found for this project</div>
            </div>
          )}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={tasks}
          rowKey="_id"
          loading={loading}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Total ${total} tasks`,
          }}
        />
      )}
    </Card>

      <Modal
        title={editingTask ? 'Edit Task' : 'Create New Task'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="title"
            label="Task Title"
            rules={[{ required: true, message: 'Please enter task title' }]}
          >
            <Input placeholder="Enter task title" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter task description' }]}
          >
            <TextArea rows={4} placeholder="Enter task description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="assignedTo"
                label="Assign To"
                rules={[{ required: true, message: 'Please select assignee' }]}
              >
                <Select placeholder="Select team member">
                  {getProjectTeamMembers().map(employee => (
                    <Option key={employee._id} value={employee._id}>
                      {employee.name} ({employee.role})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="Priority"
                initialValue="medium"
              >
                <Select>
                  <Option value="low">Low</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="high">High</Option>
                  <Option value="urgent">Urgent</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="estimatedHours"
                label="Estimated Hours"
              >
                <Input type="number" min={0} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="dueDate"
                label="Due Date"
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="tags"
            label="Tags"
          >
            <Select
              mode="tags"
              placeholder="Add tags"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingTask ? 'Update Task' : 'Create Task'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Task View Modal with Comments */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MessageOutlined />
            Task Details & Comments
          </div>
        }
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
        width={800}
      >
        {viewingTask && (
          <div>
            {/* Task Details Section */}
            <Card size="small" style={{ marginBottom: '16px' }}>
              <Typography.Title level={4}>{viewingTask.title}</Typography.Title>
              <Typography.Paragraph>{viewingTask.description}</Typography.Paragraph>
              
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <strong>Assigned to:</strong> {viewingTask.assignedToName}
                </Col>
                <Col span={12}>
                  <strong>Priority:</strong> 
                  <Tag color={getPriorityColor(viewingTask.priority)} style={{ marginLeft: '8px' }}>
                    {viewingTask.priority.toUpperCase()}
                  </Tag>
                </Col>
                <Col span={12}>
                  <strong>Status:</strong>
                  <Tag color={getStatusColor(viewingTask.status)} style={{ marginLeft: '8px' }}>
                    {viewingTask.status.toUpperCase()}
                  </Tag>
                </Col>
                <Col span={12}>
                  <strong>Due Date:</strong> {viewingTask.dueDate ? dayjs(viewingTask.dueDate).format('MMM DD, YYYY') : 'Not set'}
                </Col>
                <Col span={12}>
                  <strong>Estimated Hours:</strong> {viewingTask.estimatedHours || 0}h
                </Col>
                <Col span={12}>
                  <strong>Actual Hours:</strong> {viewingTask.actualHours || 0}h
                </Col>
              </Row>
            </Card>

            <Divider>Comments</Divider>

            {/* Comments Section */}
            <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '16px' }}>
              {taskComments.length > 0 ? (
                <List
                  dataSource={Array.isArray(taskComments) ? taskComments : []}
                  renderItem={(comment) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar icon={<UserOutlined />} />}
                        title={
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{comment.userName || comment.userId}</span>
                            <span style={{ fontSize: '12px', color: '#999' }}>
                              {dayjs(comment.createdAt).format('MMM DD, YYYY HH:mm')}
                            </span>
                          </div>
                        }
                        description={comment.comment}
                      />
                    </List.Item>
                  )}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  No comments yet. Be the first to add a comment!
                </div>
              )}
            </div>

            {/* Add Comment Form */}
            <Form
              form={commentForm}
              onFinish={handleAddComment}
              layout="vertical"
            >
              <Form.Item
                name="comment"
                rules={[{ required: true, message: 'Please enter a comment' }]}
              >
                <TextArea 
                  rows={3} 
                  placeholder="Add a comment..."
                  style={{ resize: 'none' }}
                />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Button type="primary" htmlType="submit" icon={<SendOutlined />}>
                  Add Comment
                </Button>
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TaskManagement;
